
import { Container } from "reactstrap";
import {
    FiPlus,
    FiMinus,
    FiCode,
    FiBriefcase,
    FiBookOpen,
    FiHelpCircle
} from "react-icons/fi";
import "./CAD_FAQ.css";
import { useState } from "react";

const defaultFaqs = [
    {
        question: "What is CAD Customization & Automation?",
        icon: <FiCode />,
        answer: (
            <>
                <p>
                    <strong>CAD Customization</strong> refers to tailoring CAD
                    software like <span>SolidWorks, NX, Inventor, and AutoCAD</span>{" "}
                    to fit specific engineering workflows. This is done by
                    writing scripts or programs using APIs provided by the CAD
                    software.
                </p>

                <p>
                    <strong>CAD Automation</strong> involves using programming
                    to automate repetitive design tasks — such as generating
                    <span> models, drawings, or assemblies</span> — without
                    manually doing them each time.
                </p>

                <div className="cad-faq-highlight">
                    <strong>In simple terms:</strong>
                    <span>
                        Customize the CAD software to your workflow and
                        automate repetitive engineering tasks.
                    </span>
                </div>
            </>
        )
    },

    {
        question: "How Much Salary?",
        icon: <FiBriefcase />,
        answer: (
            <>
                <p>
                    Professionals skilled in CAD Automation earn{" "}
                    <strong>40% to 100% more</strong> than manual CAD
                    designers.
                </p>

                <div className="cad-faq-stat">
                    <span className="cad-faq-stat-value">₹8–20+ LPA</span>
                    <span className="cad-faq-stat-label">
                        Experienced engineers with CAD API knowledge
                    </span>
                </div>

                <p>
                    Experienced engineers with CAD API knowledge can earn{" "}
                    <strong>₹8–20+ LPA</strong> or work on{" "}
                    <strong>high-paying global freelance projects</strong>.
                </p>
            </>
        )
    },

    {
        question: "I Have CAD Customization & Automation Design Experience?",
        icon: <FiBookOpen />,
        answer: (
            <>
                <p>
                    Hands-on experience in{" "}
                    <strong>CAD Customization & Automation</strong> using APIs
                    like:
                </p>

                <div className="cad-faq-tags">
                    <span>NX Open</span>
                    <span>Inventor API</span>
                    <span>AutoCAD .NET</span>
                </div>

                <p>
                    Built tools to automate:
                </p>

                <ul>
                    <li>Part modeling</li>
                    <li>Drawing generation</li>
                    <li>Parameter-driven assemblies</li>
                </ul>

                <div className="cad-faq-highlight">
                    <strong>Programming:</strong>
                    <span>C# and Windows Forms</span>
                </div>
            </>
        )
    },

    {
        question: "Job Guaranteed?",
        icon: <FiBriefcase />,
        answer: (
            <>
                <div className="cad-faq-highlight cad-faq-warning">
                    <strong>Important:</strong>
                    <span>
                        While no course can guarantee a job, CAD Automation
                        skills are in high demand.
                    </span>
                </div>

                <p>
                    Engineers with these skills have a much higher chance of
                    placement, especially in{" "}
                    <strong>R&D and product companies</strong>.
                </p>
            </>
        )
    },

    {
        question: "Which programming language?",
        icon: <FiCode />,
        answer: (
            <>
                <div className="cad-language-list">

                    <div className="cad-language-item">
                        <strong>C#</strong>
                        <span>
                            Most widely used for SolidWorks, AutoCAD, Inventor
                        </span>
                    </div>

                    <div className="cad-language-item">
                        <strong>C++</strong>
                        <span>
                            Used in CATIA (CAA), NX Open, and
                            performance-heavy tasks
                        </span>
                    </div>

                    <div className="cad-language-item">
                        <strong>Python</strong>
                        <span>
                            Used in NX, Fusion 360, and for quick scripting
                        </span>
                    </div>

                    <div className="cad-language-item">
                        <strong>VBA</strong>
                        <span>
                            Used for basic automation in Excel-connected CAD
                            tools
                        </span>
                    </div>

                </div>

                <div className="cad-faq-highlight">
                    <strong>Most in-demand:</strong>
                    <span>
                        C# and C++ are the most in-demand for industry-level
                        automation.
                    </span>
                </div>
            </>
        )
    },

    {
        question: "Job opportunities?",
        icon: <FiBriefcase />,
        answer: (
            <>
                <p>
                    CAD Automation opens doors to:
                </p>

                <div className="cad-faq-opportunities">
                    <span>R&D Teams</span>
                    <span>Design Automation</span>
                    <span>Product Development</span>
                    <span>Global Freelance Projects</span>
                </div>

                <p>
                    Top companies seek engineers skilled in tools like{" "}
                    <strong>
                        SolidWorks API, NX Open, Inventor API, and AutoCAD
                        .NET
                    </strong>.
                </p>

                <div className="cad-faq-highlight">
                    <strong>Key skills:</strong>
                    <span>
                        CAD APIs + Programming + Engineering Knowledge
                    </span>
                </div>
            </>
        )
    }
];

function CAD_FAQ({
    title = "Frequently Asked Questions",
    subtitle = "Frequently asked questions and answers.",
    faqs = defaultFaqs
}) {
    const [openIndex, setOpenIndex] = useState(0);

    const toggleFaq = (index) => {
        setOpenIndex(openIndex === index ? -1 : index);
    };

    return (
        <section className="cad-faq-section">
            <Container>

                {/* Header */}
                <div className="cad-faq-header text-center">
                    <span className="cad-faq-badge">
                        <FiHelpCircle size={16} />
                        FAQ
                    </span>

                    <h2>{title}</h2>

                    <p>{subtitle}</p>

                    <div className="cad-faq-divider">
                        <span></span>
                    </div>
                </div>

                {/* FAQ List */}
                <div className="cad-faq-list">
                    {faqs.map((faq, index) => {
                        const isOpen = openIndex === index;

                        return (
                            <div
                                className={`cad-faq-item ${isOpen ? "active" : ""
                                    }`}
                                key={index}
                            >
                                <button
                                    type="button"
                                    className="cad-faq-question"
                                    onClick={() => toggleFaq(index)}
                                    aria-expanded={isOpen}
                                >
                                    <div className="cad-faq-question-left">

                                        <div className="cad-faq-icon">
                                            {faq.icon}
                                        </div>

                                        <div>
                                            <span className="cad-faq-number">
                                                {String(index + 1).padStart(
                                                    2,
                                                    "0"
                                                )}
                                            </span>

                                            <h3>{faq.question}</h3>
                                        </div>

                                    </div>

                                    <div className="cad-faq-toggle">
                                        {isOpen ? (
                                            <FiMinus />
                                        ) : (
                                            <FiPlus />
                                        )}
                                    </div>
                                </button>

                                <div
                                    className={`cad-faq-answer ${isOpen ? "show" : ""
                                        }`}
                                >
                                    <div className="cad-faq-answer-inner">
                                        {faq.answer}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Bottom CTA */}
                <div className="cad-faq-bottom">
                    <div>
                        <strong>Still have questions?</strong>
                        <span>
                            Get in touch with us to learn more about CAD
                            Customization & Automation.
                        </span>
                    </div>

                    <div className="cad-faq-bottom-icon">
                        <FiHelpCircle />
                    </div>
                </div>

            </Container>
        </section>
    );
}

export default CAD_FAQ;
